import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  HelpTip,
  cn,
  dateSourceLabel,
  formatDateOnly,
  showsStatementBasis,
  StatementBasisEditor,
  StatementBasisLine,
  fileIndicatorsOf,
} from '@dataroom/shared';
import type {
  CategoryFolderPhase,
  CategoryNode,
  ClassificationProposal,
  RoomDocument,
  StatementBasis,
} from '@dataroom/shared';
import { Modal } from './Modal';
import { CategoryPicker } from './CategoryPicker';
import { AddCategoryForm } from './AddCategoryForm';
import { ClassificationStatusBadge, Spinner } from './ClassificationStatusBadge';
import { FilingLevel } from './FilingTrail';
import { NewCategoryHere } from './NewCategoryHere';
import {
  categoryNodesById,
  categoryParents,
  categoryPathLabel,
  categoryPathsById,
  deepestCategoryId,
  pathIdsTo,
} from '@dataroom/shared';
import { reviewRound, suggestionLabel } from '@/lib/classification-review';
import { withoutInstitutionLevel } from '@/lib/new-category';
import {
  filingTrail,
  idsWhenChoosing,
  levelNumber,
  offersNewCategoryAt,
  roundFrom,
  trailLabel,
  type LevelView,
} from '@/lib/filing-trail';

interface DocumentDetailModalProps {
  /** The document being inspected, or null when the panel is closed. */
  document: RoomDocument | null;
  /** The room's category hierarchy — resolves a proposal's `tagId` to a readable
   * category path, gives each review round its choices, and is the list for
   * "choose a different branch". */
  categories: CategoryNode[];
  /** The room checklist's phases (GET …/categories `phases`): the first crumb of
   * the filing breadcrumb. Omitted = the breadcrumb starts at the categories. */
  phases?: CategoryFolderPhase[];
  /** ALL of the room's pending proposals; filtered to this document here. */
  proposals: ClassificationProposal[];
  /** Whether the pending-proposals query is still loading. */
  proposalsLoading: boolean;
  /** The proposal id whose accept is in flight, or null. */
  approvingId: string | null;
  /** The proposal id whose reject is in flight, or null. */
  rejectingId: string | null;
  /** Whether a re-classify request is in flight. */
  reclassifying: boolean;
  /** Whether a category save (a review round or a branch change) is in flight. */
  savingCategories: boolean;
  /** A server/validation error from the last action, or null. */
  actionError: string | null;
  onApprove: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  /** Re-run the AI on this one document (POST .../reclassify). */
  onReclassify: () => void;
  /** Save the document's full category set (the Modify-attributes PATCH, which
   * records the person's decision per tag); `onSaved` runs once it lands. */
  onSetCategories: (categoryTagIds: string[], onSaved: () => void) => void;
  /** Correct the financial statement's accounting basis (ADR-0053); `onSaved`
   * runs once it lands. Omitted = the basis is shown without the control. */
  onSetStatementBasis?: (basis: StatementBasis, onSaved: () => void) => void;
  /** Whether a basis correction is in flight. */
  savingBasis?: boolean;
  /** The last basis correction's error, or null. */
  basisError?: string | null;
  /** Open the in-app file viewer for this document (owner ask: actually SEE the
   * file to verify which document it is). */
  onOpenPreview: () => void;
  /** Opens the redaction review of this document (its badge shows "Review
   * redaction" while the redaction needs a person). */
  onReviewRedaction?: () => void;
  /** The full-page review queue opened at this document; when set, the footer
   * links there (the queue shows the file beside the suggestions). */
  reviewQueueHref?: string;
  /** The "Possible new version of …" suggestions involving this document
   * (ADR-0031), rendered by the page (it owns their query and actions). */
  versionSuggestions?: ReactNode;
  /** The document's version history (ADR-0031), rendered by the page. */
  versionHistory?: ReactNode;
  onClose: () => void;
}

/** Render a [0,1] confidence as a whole-number percentage (e.g. 0.872 → "87%"). */
function pct(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

/** One line of plain-English guidance per classification state. */
function guidanceFor(status: RoomDocument['classificationStatus'], filed: boolean): string | null {
  switch (status) {
    case 'low_confidence':
      return filed
        ? 'The AI filed this as far as it was sure. Choose the next level below, or pick a different branch if it went the wrong way.'
        : "The AI wasn't confident about this file. Look at it, then choose its category below.";
    case 'failed':
      return "The AI couldn't classify this file, not even by its name. Re-classify to try again, or choose its category below.";
    case 'processing':
      return 'The AI is classifying this file now. This panel updates on its own when it finishes.';
    case 'not_classifiable':
      return 'This is deal paperwork (an NDA, an LOI or an agreement issued for signature). It is never sent to the AI and is kept out of the DD library.';
    default:
      return null;
  }
}

/**
 * The per-document classification REVIEW (issue #131; ADR-0018 review flow;
 * tier-by-tier rounds since ADR-0019's 2026-10-02 update). Opened from the Data
 * room list (the row's classification badge, its name for a needs-review file,
 * or "Review") and from the upload status screen. In one place the admin can:
 *   - see what the document is filed under — its category path, period and date,
 *   - continue the classification ONE LEVEL AT A TIME from the deepest category
 *     the AI filed: the choices are that category's children (the AI's
 *     next-level suggestion marked), and choosing one files it and, if it still
 *     has levels below, offers those next,
 *   - choose a different branch when the AI's tiers are wrong (replaces them),
 *   - add a NEW category the room's checklist did not think of (owner ask
 *     2026-10-12) inside the deepest filed category or another one, and file
 *     the document there in one action (AddCategoryForm),
 *   - accept or reject the AI's other suggestions (period, date, …),
 *   - open the file in the in-app viewer, and
 *   - RE-CLASSIFY it (the list polls until the job lands, and this panel tracks
 *     the live document by id, so it shows Processing then the new result).
 * Every category choice is saved through the category PATCH, which records it
 * as the person's decision. Presentational: the page owns the document, the
 * proposals query, the mutations, and the pending/error state.
 */
export function DocumentDetailModal({
  document,
  categories,
  phases,
  proposals,
  proposalsLoading,
  approvingId,
  rejectingId,
  reclassifying,
  savingCategories,
  actionError,
  onApprove,
  onReject,
  onReclassify,
  onSetCategories,
  onSetStatementBasis,
  savingBasis = false,
  basisError = null,
  onOpenPreview,
  onReviewRedaction,
  reviewQueueHref,
  versionSuggestions,
  versionHistory,
  onClose,
}: DocumentDetailModalProps) {
  const paths = useMemo(() => categoryPathsById(categories), [categories]);
  const parentOf = useMemo(() => categoryParents(categories), [categories]);
  const byId = useMemo(() => categoryNodesById(categories), [categories]);
  const names = useMemo(
    () => new Map([...byId].map(([id, node]) => [id, node.name] as const)),
    [byId],
  );
  // The level being chosen: the deepest filed category's children, or a level
  // stepped back up to with the breadcrumb (nothing saved until a choice); and
  // whether "+ New category here" has its name field open.
  const [view, setView] = useState<LevelView>({ kind: 'filed' });
  const [newOpen, setNewOpen] = useState(false);
  // "Choose a different branch": closed until asked for; one category chosen.
  const [picking, setPicking] = useState(false);
  const [branch, setBranch] = useState<string | null>(null);
  const branchRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // The opener is gone in branch mode: start in the picker's search box.
    if (picking)
      branchRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
  }, [picking]);
  // "Add a new category…": the form is mounted only while open (it owns its own
  // create-and-file mutation); its in-flight state disables the panel's other
  // actions, and the result is announced once it lands.
  const [adding, setAdding] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [added, setAdded] = useState<{ filedPath: string; created: boolean } | null>(null);
  const addedRef = useRef<HTMLParagraphElement>(null);
  const onAddBusyChange = useCallback((b: boolean) => setAddBusy(b), []);
  useEffect(() => {
    // The form unmounts on success: move focus to the confirmation.
    if (added) addedRef.current?.focus();
  }, [added]);

  // Proposals for THIS document, highest-confidence first.
  const docProposals = useMemo(() => {
    if (!document) return [];
    return proposals
      .filter((p) => p.documentId === document.id)
      .sort((a, b) => b.confidence - a.confidence);
  }, [proposals, document]);

  if (!document) return null;

  const status = document.classificationStatus;
  const processing = status === 'processing';
  const busy =
    approvingId !== null || rejectingId !== null || reclassifying || savingCategories || addBusy;
  const filedIds = document.categoryTagIds ?? [];

  // Filed under: the deepest category's path, plus any category that is not on
  // it (a person may file a document in two places), then the period and date.
  const deepest = deepestCategoryId(filedIds, parentOf);
  const onDeepestPath = new Set(deepest ? pathIdsTo(deepest, parentOf) : []);
  // Each is the full path from the phase down, as the breadcrumb reads it.
  const filedPaths = deepest
    ? [paths.has(deepest) ? trailLabel(deepest, parentOf, names, phases) : deepest]
    : document.categoryPath.length > 0
      ? [categoryPathLabel(document.categoryPath, ' › ')]
      : [];
  for (const id of filedIds) {
    if (!onDeepestPath.has(id) && paths.has(id)) {
      filedPaths.push(trailLabel(id, parentOf, names, phases));
    }
  }
  const guidance = guidanceFor(status, filedPaths.length > 0);

  const round = processing
    ? null
    : reviewRound(document, categories, parentOf, docProposals, roundFrom(view, phases));
  const offered = new Set(round?.options.map((o) => o.id) ?? []);
  const otherSuggestions = docProposals.filter((p) => !offered.has(p.tagId));
  const offersNew = round !== null && offersNewCategoryAt(round.parentId, byId, parentOf);

  const fileUnder = (id: string) => {
    setNewOpen(false);
    onSetCategories(
      idsWhenChoosing(filedIds, deepest, parentOf, round?.parentId ?? null, id),
      // Filed: the next level continues from where it now is.
      () => setView({ kind: 'filed' }),
    );
  };
  const saveBranch = () => {
    if (branch) {
      onSetCategories(withoutInstitutionLevel(categories, pathIdsTo(branch, parentOf)), () =>
        setPicking(false),
      );
    }
  };
  const canFileBranch = branch !== null && !busy;

  return (
    <Modal
      title="Review classification"
      description={document.name}
      onClose={onClose}
      size="lg"
      // Branch mode: the picker fills the dialog (owner 2026-10-15: the "Wrong
      // branch" box was "way too small"); the body stops being one long scroll.
      bodyClassName={picking ? 'flex flex-col' : undefined}
      footer={
        <>
          {actionError ? (
            <p className="text-sm text-destructive" role="alert">
              {actionError}
            </p>
          ) : (
            <span className="text-xs text-muted-foreground">
              Each choice files the document one level deeper and is recorded as yours.
            </span>
          )}
          <span className="flex gap-2">
            {reviewQueueHref ? (
              <Link
                to={reviewQueueHref}
                className="inline-flex h-8 items-center rounded-md px-3 text-[13px] font-semibold text-primary hover:underline"
              >
                Open the review queue
              </Link>
            ) : null}
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </span>
        </>
      }
    >
      <div className={cn('text-sm', picking ? 'flex min-h-0 flex-1 flex-col gap-4' : 'space-y-5')}>
        {/* While a different branch is being chosen, only what it is filed under
            and the picker are shown, so the picker gets the dialog's height. */}
        {!picking ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {status || document.processingStatus ? (
                <ClassificationStatusBadge
                  status={status}
                  processingStatus={document.processingStatus}
                  indicators={fileIndicatorsOf(document)}
                  paperworkKind={document.paperworkKind}
                  unpacked={Boolean(document.archive)}
                  inFolder={document.categoryPath.length > 0}
                  onReviewRedaction={onReviewRedaction}
                  subject={document.name}
                />
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {document.downloadReady ? (
                <Button size="sm" variant="outline" onClick={onOpenPreview}>
                  View file
                </Button>
              ) : (
                <span className="self-center text-xs text-muted-foreground">
                  Not available to view yet
                </span>
              )}
              {/* A buyer's LOI is never sent to the AI (the server refuses it too). */}
              {document.aiClassifiable !== false ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onReclassify}
                  disabled={busy || processing}
                  title="Re-run the AI on this file. Categories you set yourself are kept."
                >
                  {reclassifying || processing ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Spinner />
                      Re-classifying…
                    </span>
                  ) : (
                    'Re-classify'
                  )}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {!picking && guidance ? <p className="text-muted-foreground">{guidance}</p> : null}

        {!picking ? versionSuggestions : null}

        {!picking && document.folderPath ? (
          <p className="text-xs text-muted-foreground">
            Uploaded from: <span className="break-all">{document.folderPath}</span>
          </p>
        ) : null}

        <div>
          <p className="mb-2 font-medium">Filed under</p>
          {filedPaths.length > 0 || document.period || document.dataDate || document.executionDate ? (
            <ul className="flex flex-wrap gap-1.5" aria-label="Tags">
              {filedPaths.map((name, i) => (
                <li
                  key={`${name}-${i}`}
                  className="rounded-full bg-st-certified px-2.5 py-0.5 text-xs font-medium text-st-certified-fg"
                >
                  {name}
                </li>
              ))}
              {document.period ? (
                <li className="rounded-full bg-st-processing px-2.5 py-0.5 text-xs font-medium text-st-processing-fg">
                  Period: {document.period}
                </li>
              ) : null}
              {document.dataDate ? (
                <li className="rounded-full bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-ink-2">
                  Date: {formatDateOnly(document.dataDate)}
                </li>
              ) : null}
              {/* A contract's execution date — the date the last party signed. */}
              {document.executionDate ? (
                <li className="rounded-full bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-ink-2">
                  Executed {formatDateOnly(document.executionDate)}
                </li>
              ) : null}
            </ul>
          ) : (
            <p className="text-muted-foreground">Not filed in any category yet.</p>
          )}
        </div>

        {/* A financial statement's accounting basis (ADR-0053): read from the
            document by the AI; the admin may correct it (audited). */}
        {!picking && document.aiClassifiable !== false && showsStatementBasis(document) ? (
          <div>
            <div className="mb-2 flex items-center gap-1">
              <p className="font-medium">Accounting basis</p>
              <HelpTip id="admin.review.accounting-basis" className="ml-1" />
            </div>
            {onSetStatementBasis ? (
              <StatementBasisEditor
                value={document.statementBasis}
                showConfidence
                pending={savingBasis}
                error={basisError}
                onSave={onSetStatementBasis}
              />
            ) : (
              <StatementBasisLine value={document.statementBasis} showConfidence />
            )}
          </div>
        ) : null}

        {!picking && round ? (
          <div>
            <p className="mb-2 font-medium">
              {round.parentId ? 'Next level' : 'Choose a category'}
            </p>
            <FilingLevel
              crumbs={filingTrail(
                { parentId: round.parentId, phaseId: view.kind === 'phase' ? view.phaseId : null },
                parentOf,
                names,
                phases,
              )}
              level={levelNumber(round.parentId, parentOf)}
              onStep={(next) => {
                setNewOpen(false);
                setView(next);
              }}
              onReturn={
                view.kind === 'filed'
                  ? undefined
                  : {
                      label: (deepest ? names.get(deepest) : null) ?? 'where it is filed',
                      go: () => {
                        setNewOpen(false);
                        setView({ kind: 'filed' });
                      },
                    }
              }
            >
              <ul className="mt-2 space-y-1.5" aria-label="Next level choices">
                {round.options.map((option) => (
                  <li
                    key={option.id}
                    className="flex items-center justify-between gap-3 rounded border px-3 py-2"
                  >
                    <span className="min-w-0">
                      <span className="font-medium">{option.name}</span>
                      {option.suggestion ? (
                        <span className="ml-2 rounded-full bg-st-check px-2 py-0.5 text-xs font-medium text-st-check-fg">
                          AI suggests · {pct(option.suggestion.confidence)}
                        </span>
                      ) : null}
                      {option.hasChildren ? (
                        <span className="block text-xs text-muted-foreground">
                          More levels below
                        </span>
                      ) : null}
                    </span>
                    <Button
                      size="sm"
                      variant={option.suggestion ? 'default' : 'outline'}
                      onClick={() => fileUnder(option.id)}
                      disabled={busy}
                      aria-label={`File under ${option.name}`}
                    >
                      File here
                    </Button>
                  </li>
                ))}
              </ul>
              {offersNew ? (
                <NewCategoryHere
                  key={round.parentId ?? 'top'}
                  roomId={document.roomId}
                  categories={categories}
                  parentId={round.parentId}
                  parentName={round.parentId ? (names.get(round.parentId) ?? null) : null}
                  open={newOpen}
                  onOpenChange={setNewOpen}
                  disabled={busy}
                  onChosen={(category) => fileUnder(category.id)}
                />
              ) : null}
            </FilingLevel>
          </div>
        ) : null}

        {!picking ? (
          <div>
            <p className="mb-2 font-medium">{round ? 'Other AI suggestions' : 'AI suggestions'}</p>
            {proposalsLoading ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : otherSuggestions.length > 0 ? (
              <ul className="space-y-1.5">
                {otherSuggestions.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-3 rounded border px-3 py-2"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{suggestionLabel(p, paths)}</span>
                      <span className="text-xs text-muted-foreground">
                        {dateSourceLabel(p) ? (
                          // A date READ from the file name / statement / contract: its
                          // source is the evidence, not a percentage (owner 2026-10-15).
                          `Read ${dateSourceLabel(p)}`
                        ) : (
                          <>
                            {pct(p.confidence)} confidence
                            {p.reasoning ? ` — ${p.reasoning}` : ''}
                          </>
                        )}
                      </span>
                    </span>
                    <span className="flex shrink-0 gap-2">
                      <Button size="sm" onClick={() => onApprove(p.id)} disabled={busy}>
                        {approvingId === p.id ? 'Accepting…' : 'Accept'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onReject(p.id)}
                        disabled={busy}
                      >
                        {rejectingId === p.id ? 'Rejecting…' : 'Reject'}
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">
                {processing
                  ? 'Suggestions appear here when the AI finishes.'
                  : 'No other suggestions waiting for review.'}
              </p>
            )}
          </div>
        ) : null}

        {/* Deal paperwork is never filed (the server refuses it): no branch picker. */}
        {document.aiClassifiable !== false ? (
          <div ref={branchRef} className={cn(picking && 'flex min-h-0 flex-1 flex-col')}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
              <p className="font-medium">Wrong branch?</p>
              {!picking ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBranch(null);
                    setPicking(true);
                    setAdding(false);
                  }}
                  disabled={busy}
                >
                  Choose a different branch…
                </Button>
              ) : (
                // The one action sits up here, across from the heading, so it
                // is never lost below a long list (owner ask 2026-10-15). It
                // stays focusable while unavailable (aria-disabled, not
                // disabled) so its title says why.
                <span className="flex gap-2" data-testid="branch-actions">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPicking(false)}
                    disabled={savingCategories}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant={canFileBranch ? 'default' : 'secondary'}
                    aria-disabled={!canFileBranch}
                    title={
                      canFileBranch
                        ? `File it under ${paths.get(branch!) ?? 'that category'}`
                        : branch
                          ? 'Wait for the current change to finish'
                          : 'Choose a branch first'
                    }
                    className={cn(!canFileBranch && 'cursor-not-allowed text-muted-foreground')}
                    onClick={() => {
                      if (canFileBranch) saveBranch();
                    }}
                  >
                    {savingCategories ? 'Saving…' : 'File in this branch'}
                  </Button>
                </span>
              )}
            </div>
            {picking ? (
              <div className="flex min-h-0 flex-1 flex-col gap-2 rounded border p-3">
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  {branch
                    ? `Files it under ${paths.get(branch) ?? 'that category'}, replacing its categories.`
                    : 'Pick the category it belongs in.'}
                </p>
                {/* The tree takes the rest of the dialog; its search box stays
                    pinned at the top of this scroll box (sticky). */}
                <div
                  className="min-h-[12rem] flex-1 overflow-y-auto"
                  data-testid="branch-picker-scroll"
                >
                  <CategoryPicker
                    categories={categories}
                    selected={branch ? new Set([branch]) : new Set()}
                    onToggle={(id) => setBranch((prev) => (prev === id ? null : id))}
                    disabled={savingCategories}
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                If the AI went down the wrong path, pick the right category from the room&apos;s
                list — it replaces the AI&apos;s categories, from the top level down.
              </p>
            )}
          </div>
        ) : null}

        {/* A category the room's checklist did not think of (owner ask
            2026-10-12) — most often the missing third level, e.g. one card or
            account. Deal paperwork is never filed, so it never gets this. */}
        {!picking && document.aiClassifiable !== false ? (
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="font-medium">Category not listed?</p>
              {!adding ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAdded(null);
                    setPicking(false);
                    setAdding(true);
                  }}
                  disabled={busy || processing}
                >
                  Add a new category…
                </Button>
              ) : null}
            </div>
            {adding ? (
              <AddCategoryForm
                roomId={document.roomId}
                documentId={document.id}
                categories={categories}
                paths={paths}
                parentOf={parentOf}
                filedIds={filedIds}
                deepestFiledId={deepest}
                disabled={busy && !addBusy}
                onBusyChange={onAddBusyChange}
                onCancel={() => setAdding(false)}
                onDone={(result) => {
                  setAdding(false);
                  setAdded(result);
                }}
              />
            ) : added ? (
              <p
                ref={addedRef}
                tabIndex={-1}
                role="status"
                className="rounded bg-st-certified px-3 py-2 text-sm text-st-certified-fg outline-none"
              >
                Filed under <span className="font-semibold">{added.filedPath}</span>
                {added.created
                  ? '.'
                  : ' — that category already existed, so no duplicate was made.'}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Add the category the checklist didn&apos;t think of — one card, account or property,
                say — inside where it&apos;s filed now or any other category, and file the document
                there. It shares that category&apos;s audience.
              </p>
            )}
          </div>
        ) : null}

        {!picking ? versionHistory : null}
      </div>
    </Modal>
  );
}
